import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaClock,
  FaVideo,
  FaPhone,
  FaCommentAlt,
  FaEllipsisH,
  FaSearch,
} from "react-icons/fa";
import { authAPI } from "../../services/api";

import client1 from "../../assets/team-4.jpg";

export default function ExpertAppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [showDropdownId, setShowDropdownId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState({ show: false, type: '', message: '' });

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Fetch appointments from API
  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const result = await authAPI.getExpertAppointments();
      
      if (result.success) {
        setAppointments(result.data || []);
      } else {
        showToast('error', result.message || 'Failed to fetch appointments');
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      showToast('error', 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  // Format currency with Nigerian naira symbol
  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount).toLocaleString()}`;
  };

  // Format date and time
  const formatDateTime = (dateString, timeString) => {
    const date = new Date(dateString);
    const time = new Date(timeString);
    
    const dateOptions = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    
    return {
      date: date.toLocaleDateString('en-US', dateOptions),
      time: time.toLocaleTimeString('en-US', timeOptions)
    };
  };

  // Filter appointments based on active tab and search query
  const filteredAppointments = appointments.filter((appointment) => {
    // Tab filtering
    if (activeTab === "upcoming" && appointment.status !== "upcoming")
      return false;
    if (activeTab === "completed" && appointment.status !== "completed")
      return false;
    if (activeTab === "cancelled" && appointment.status !== "cancelled")
      return false;

    // Search filtering
    if (
      searchQuery &&
      !appointment.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !appointment.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    return true;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      upcoming: {
        className: 'status-upcoming',
        text: 'Upcoming'
      },
      completed: {
        className: 'status-completed',
        text: 'Completed'
      },
      cancelled: {
        className: 'status-cancelled',
        text: 'Cancelled'
      }
    };

    const config = statusConfig[status] || statusConfig.upcoming;

    return (
      <span className={`status-badge ${config.className}`}>
        {config.text}
      </span>
    );
  };

  const getConsultationIcon = (type) => {
    switch (type) {
      case 'video':
        return <FaVideo className="consultation-icon" />;
      case 'voice':
        return <FaPhone className="consultation-icon" />;
      default:
        return <FaCommentAlt className="consultation-icon" />;
    }
  };

  if (loading) {
    return (
      <div className="expert-appointments-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="expert-appointments-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification ${toast.type === 'success' ? 'toast-success' : 'toast-error'}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="appointments-header">
        <div className="header-content">
          <h1>Appointments</h1>
          <p>Manage your consultation appointments with clients</p>
        </div>
      </div>

      {/* Search and Tabs */}
      <div className="appointments-controls">
        <div className="search-container">
          <FaSearch size={16} />
          <input
            type="text"
            placeholder="Search appointments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === "upcoming" ? "active" : ""}`}
            onClick={() => setActiveTab("upcoming")}
          >
            Upcoming ({appointments.filter(a => a.status === 'upcoming').length})
          </button>
          <button
            className={`tab-button ${activeTab === "completed" ? "active" : ""}`}
            onClick={() => setActiveTab("completed")}
          >
            Completed ({appointments.filter(a => a.status === 'completed').length})
          </button>
          <button
            className={`tab-button ${activeTab === "cancelled" ? "active" : ""}`}
            onClick={() => setActiveTab("cancelled")}
          >
            Cancelled ({appointments.filter(a => a.status === 'cancelled').length})
          </button>
        </div>
      </div>

      {/* Appointments List */}
      <div className="appointments-list">
        {filteredAppointments.length === 0 ? (
          <div className="empty-state">
            <FaCalendarAlt size={48} className="empty-icon" />
            <h3>No appointments found</h3>
            <p>
              {appointments.length === 0
                ? "You don't have any appointments yet."
                : "No appointments match your current filters."
              }
            </p>
          </div>
        ) : (
          filteredAppointments.map((appointment) => {
            const { date, time } = formatDateTime(appointment.date, appointment.time);
            return (
              <div key={appointment.appointment_id} className="appointment-card">
                <div className="appointment-header">
                  <div className="client-info">
                    <img 
                      src={appointment.client_image || client1} 
                      alt={appointment.client_name} 
                      className="client-avatar" 
                    />
                    <div className="client-details">
                      <h3>{appointment.client_name}</h3>
                      <p className="appointment-topic">{appointment.description}</p>
                    </div>
                  </div>
                  <div className="appointment-actions">
                    {getStatusBadge(appointment.status)}
                    <div className="dropdown-container">
                      <button
                        className="dropdown-trigger"
                        onClick={() => setShowDropdownId(
                          showDropdownId === appointment.appointment_id ? null : appointment.appointment_id
                        )}
                      >
                        <FaEllipsisH />
                      </button>
                      {showDropdownId === appointment.appointment_id && (
                        <div className="dropdown-menu">
                          <Link 
                            to={`/expert-dashboard/appointments/${appointment.appointment_id}`}
                            className="dropdown-item"
                          >
                            View Details
                          </Link>
                          <Link 
                            to={`/expert-dashboard/messages?client_name=${encodeURIComponent(appointment.client_name)}&appointment_id=${appointment.appointment_id}`}
                            className="dropdown-item"
                          >
                            Message Client
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="appointment-details">
                  <div className="detail-item">
                    <FaCalendarAlt size={14} />
                    <span>{date}</span>
                  </div>
                  <div className="detail-item">
                    <FaClock size={14} />
                    <span>{time}</span>
                  </div>
                  <div className="detail-item">
                    {getConsultationIcon(appointment.consultation_type)}
                    <span className="consultation-type">
                      {appointment.consultation_type === 'video' ? 'Video Call' : 
                       appointment.consultation_type === 'voice' ? 'Voice Call' : 'Chat'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="amount">{formatCurrency(appointment.amount_paid)}</span>
                  </div>
                </div>

                <div className="appointment-footer">
                  <div className="appointment-id">
                    ID: {appointment.payment_reference}
                  </div>
                  <Link 
                    to={`/expert-dashboard/appointments/${appointment.appointment_id}`}
                    className="view-details-btn"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            );
          })
        )}
      </div>

      <style jsx>{`

        .appointments-header {
          margin-bottom: 32px;
        }

        .header-content h1 {
          margin: 0 0 8px 0;
          font-size: 28px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .header-content p {
          margin: 0;
          color: #666;
          font-size: 16px;
        }

        .appointments-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          gap: 20px;
          flex-wrap: wrap;
        }

        .search-container {
          display: flex;
          align-items: center;
          gap: 8px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px 16px;
          flex: 1;
          min-width: 250px;
        }

        .search-input {
          border: none;
          outline: none;
          background: transparent;
          width: 100%;
          font-size: 14px;
        }

        .tabs-container {
          display: flex;
          background: #f3f4f6;
          border-radius: 8px;
          padding: 4px;
        }

        .tab-button {
          padding: 8px 16px;
          border: none;
          background: transparent;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          transition: all 0.2s;
        }

        .tab-button.active {
          background: white;
          color: #0e4410;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .appointments-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .appointment-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          transition: box-shadow 0.3s;
        }

        .appointment-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .appointment-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .client-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .client-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
        }

        .client-details h3 {
          margin: 0 0 4px 0;
          font-size: 18px;
          color: #1a1a1a;
        }

        .appointment-topic {
          margin: 0;
          color: #666;
          font-size: 14px;
        }

        .appointment-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-upcoming {
          background: #fef3c7;
          color: #f59e0b;
        }

        .status-completed {
          background: #dcfce7;
          color: #16a34a;
        }

        .status-cancelled {
          background: #fee2e2;
          color: #ef4444;
        }

        .dropdown-container {
          position: relative;
        }

        .dropdown-trigger {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 4px;
          color: #6b7280;
        }

        .dropdown-trigger:hover {
          background: #f3f4f6;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 10;
          min-width: 150px;
        }

        .dropdown-item {
          display: block;
          padding: 12px 16px;
          color: #374151;
          text-decoration: none;
          font-size: 14px;
          transition: background-color 0.2s;
        }

        .dropdown-item:hover {
          background: #f9fafb;
        }

        .appointment-details {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #f3f4f6;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #6b7280;
          font-size: 14px;
        }

        .consultation-icon {
          color: #0e4410;
        }

        .consultation-type {
          text-transform: capitalize;
        }

        .amount {
          font-weight: 600;
          color: #1a1a1a;
        }

        .appointment-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .appointment-id {
          font-size: 12px;
          color: #9ca3af;
          font-family: monospace;
        }

        .view-details-btn {
          padding: 8px 16px;
          background: #0e4410;
          color: white;
          text-decoration: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .view-details-btn:hover {
          background: #0a330a;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          color: #d1d5db;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          color: #374151;
        }

        .empty-state p {
          margin: 0;
          color: #6b7280;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #0e4410;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .toast-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1001;
          border-radius: 8px;
          padding: 16px 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          animation: slideIn 0.3s ease-out;
        }

        .toast-success {
          background: #22c55e;
          color: white;
        }

        .toast-error {
          background: #ef4444;
          color: white;
        }

        .toast-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .toast-icon {
          font-weight: bold;
          font-size: 16px;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @media (max-width: 768px) {
          .expert-appointments-container {
            padding: 16px;
          }

          .appointments-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .search-container {
            min-width: auto;
          }

          .tabs-container {
            justify-content: center;
          }

          .appointment-header {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }

          .appointment-actions {
            justify-content: space-between;
          }

          .appointment-details {
            flex-direction: column;
            gap: 8px;
          }

          .appointment-footer {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}